import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { X, ArrowUp, ArrowDown, Reply, Smile, Trash2, Pencil, Check, MessageCircle, Users, Link2, MoreVertical, LogOut, EyeOff, Eye, Ban, ShieldOff, Loader2 } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { cn } from '@/lib/utils'
import { useAuth } from '@/contexts/auth-context'
import { useCommunityChat } from '@/hooks/useCommunityChat'
import { useQuery } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import type { Id } from '../../../convex/_generated/dataModel'

const EMOJI_QUICK = ['👍', '❤️', '😂', '🔥', '👀', '🎬', '💯', '🙌']

const URL_REGEX = /https?:\/\/[^\s<]+[^\s<.,:;"')\]!?]/g
const IMAGE_EXTENSIONS = /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i
// Build a regex that matches @MemberName for known members
function buildMentionRegex(memberNames: string[]): RegExp | null {
  if (memberNames.length === 0) return null
  const escaped = memberNames.map(n => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
  return new RegExp(`@(${escaped.join('|')})(?=\\s|$|[.,!?;:])`, 'g')
}

interface CommunityPanelProps {
  open: boolean
  onClose: () => void
}

export function CommunityPanel({ open, onClose }: CommunityPanelProps) {
  const { user } = useAuth()
  const {
    messages,
    lastSeenAt,
    isViewerAdmin,
    members,
    typingUsers,
    isMember,
    isLoading,
    replyTo,
    setReplyTo,
    join,
    leave,
    sendMessage,
    toggleReaction,
    deleteMessage,
    adminHideMessage,
    unhideMessage,
    shadowBanUser,
    unShadowBanUser,
    editMessage,
    updateTyping,
    markSeen,
  } = useCommunityChat()
  const isAdmin = isViewerAdmin
  const stats = useQuery(api.community.getStats, !isMember ? {} : 'skip')

  const [input, setInput] = useState('')
  const [showMenu, setShowMenu] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const [editingId, setEditingId] = useState<Id<'communityMessages'> | null>(null)
  const [editText, setEditText] = useState('')
  const [emojiPickerFor, setEmojiPickerFor] = useState<Id<'communityMessages'> | null>(null)
  const [isScrolledUp, setIsScrolledUp] = useState(false)
  const [newMessageCount, setNewMessageCount] = useState(0)
  const [mentionQuery, setMentionQuery] = useState<string | null>(null)
  const [mentionIndex, setMentionIndex] = useState(0)
  const notificationsEnabled = typeof Notification !== 'undefined' && Notification.permission === 'granted'
  const [showNotifHelp, setShowNotifHelp] = useState(false)
  const notifHelpRef = useRef<HTMLDivElement>(null)

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const lastMessageCountRef = useRef(messages.length)
  const emojiPickerRef = useRef<HTMLDivElement>(null)
  const lastSeenAtRef = useRef<number | undefined>(undefined)
  const highlightedRef = useRef<HTMLDivElement>(null)

  // Close menu on click outside
  useEffect(() => {
    if (!showMenu) return
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setShowMenu(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showMenu])

  const handleLeave = useCallback(() => {
    setShowMenu(false)
    leave()
  }, [leave])

  // Capture lastSeenAt on first load (before markSeen updates it)
  useEffect(() => {
    if (lastSeenAt !== undefined && lastSeenAtRef.current === undefined) {
      lastSeenAtRef.current = lastSeenAt
    }
  }, [lastSeenAt])

  // Reset lastSeenAt ref when panel closes so divider recalculates on reopen
  useEffect(() => {
    if (!open) {
      lastSeenAtRef.current = undefined
    }
  }, [open])

  // Mark as seen when panel opens
  useEffect(() => {
    if (open && isMember) {
      markSeen()
    }
  }, [open, isMember, markSeen])

  // Also mark seen when new messages arrive and panel is open and not scrolled up
  useEffect(() => {
    if (open && isMember && !isScrolledUp && messages.length > 0) {
      markSeen()
    }
  }, [messages.length, open, isMember, isScrolledUp, markSeen])

  // Desktop notifications for new messages when panel is closed or tab is unfocused
  useEffect(() => {
    if (!notificationsEnabled || !isMember) return
    const prevCount = lastMessageCountRef.current
    lastMessageCountRef.current = messages.length
    if (messages.length > prevCount && prevCount > 0) {
      const newMsgs = messages.slice(prevCount)
      const isVisible = open && document.hasFocus()
      if (!isVisible) {
        for (const msg of newMsgs) {
          if (msg.userId === user?.userId) continue
          new Notification(`${msg.author.name} in Footage Community`, {
            body: msg.content.slice(0, 100),
            icon: msg.author.avatarUrl || '/logo-sq-black.png',
            tag: `community-${msg._id}`,
          })
        }
      }
    }
  }, [messages.length, open, notificationsEnabled, isMember, messages, user?.userId])

  // Auto-scroll
  const SCROLL_THRESHOLD = 200

  const checkIfScrolledUp = useCallback(() => {
    const container = scrollContainerRef.current
    if (!container) return false
    return container.scrollHeight - container.scrollTop - container.clientHeight > SCROLL_THRESHOLD
  }, [])

  const scrollToBottom = useCallback(() => {
    const container = scrollContainerRef.current
    if (container) {
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' })
    }
    setNewMessageCount(0)
    setIsScrolledUp(false)
  }, [])

  useEffect(() => {
    const newCount = messages.length
    const prevCount = lastMessageCountRef.current

    if (newCount > prevCount) {
      if (isScrolledUp) {
        setNewMessageCount(prev => prev + (newCount - prevCount))
      } else {
        setTimeout(() => {
          scrollContainerRef.current?.scrollTo({ top: scrollContainerRef.current.scrollHeight, behavior: 'smooth' })
        }, 50)
      }
    }
    lastMessageCountRef.current = newCount
  }, [messages.length, isScrolledUp])

  // Scroll to permalink message if ?msg= param present
  useEffect(() => {
    if (!open || isLoading || messages.length === 0) return
    const params = new URLSearchParams(window.location.search)
    const msgId = params.get('msg')
    if (msgId) {
      setTimeout(() => {
        const el = document.getElementById(`msg-${msgId}`)
        if (el) {
          el.scrollIntoView({ block: 'center' })
          el.classList.add('bg-yellow-50')
          setTimeout(() => el.classList.remove('bg-yellow-50'), 2000)
        }
        // Clean the URL param
        const url = new URL(window.location.href)
        url.searchParams.delete('msg')
        window.history.replaceState({}, '', url.toString())
      }, 150)
      return
    }

    // Otherwise scroll to "new" divider or bottom
    setTimeout(() => {
      if (highlightedRef.current) {
        highlightedRef.current.scrollIntoView({ block: 'center' })
      } else if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({ top: scrollContainerRef.current.scrollHeight })
      }
    }, 100)
    setTimeout(() => inputRef.current?.focus(), 150)
  }, [open, isLoading])

  const handleScroll = useCallback(() => {
    setIsScrolledUp(checkIfScrolledUp())
  }, [checkIfScrolledUp])

  // Close emoji picker on click outside
  useEffect(() => {
    if (!emojiPickerFor) return
    const handler = (e: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target as Node)) {
        setEmojiPickerFor(null)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [emojiPickerFor])

  const handleSend = useCallback(async () => {
    if (!input.trim()) return
    const text = input
    setInput('')
    setMentionQuery(null)
    updateTyping(false)
    await sendMessage(text)
  }, [input, sendMessage, updateTyping])

  const filteredMembers = useMemo(() => {
    if (mentionQuery === null) return []
    return members.filter(m =>
      m.name.toLowerCase().includes(mentionQuery) && m.userId !== user?.userId
    ).slice(0, 5)
  }, [mentionQuery, members, user?.userId])

  const insertMention = useCallback((name: string) => {
    const textarea = inputRef.current
    if (!textarea) return
    const cursorPos = textarea.selectionStart
    const textBeforeCursor = input.slice(0, cursorPos)
    const atPos = textBeforeCursor.lastIndexOf('@')
    if (atPos === -1) return
    const newInput = input.slice(0, atPos) + `@${name} ` + input.slice(cursorPos)
    setInput(newInput)
    setMentionQuery(null)
    setTimeout(() => {
      const newCursorPos = atPos + name.length + 2
      textarea.setSelectionRange(newCursorPos, newCursorPos)
      textarea.focus()
    }, 0)
  }, [input])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (filteredMembers.length > 0 && mentionQuery !== null) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setMentionIndex(i => Math.min(i + 1, filteredMembers.length - 1))
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setMentionIndex(i => Math.max(i - 1, 0))
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        insertMention(filteredMembers[mentionIndex].name)
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setMentionQuery(null)
        return
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }, [handleSend, filteredMembers, mentionQuery, mentionIndex, insertMention])

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value
    setInput(val)
    if (val.trim()) {
      updateTyping(true)
    } else {
      updateTyping(false)
    }

    // @mention detection
    const cursorPos = e.target.selectionStart
    const textBeforeCursor = val.slice(0, cursorPos)
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/)
    if (mentionMatch) {
      setMentionQuery(mentionMatch[1].toLowerCase())
      setMentionIndex(0)
    } else {
      setMentionQuery(null)
    }
  }, [updateTyping])

  // Close notification help on click outside
  useEffect(() => {
    if (!showNotifHelp) return
    const handler = (e: MouseEvent) => {
      if (notifHelpRef.current && !notifHelpRef.current.contains(e.target as Node)) {
        setShowNotifHelp(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showNotifHelp])

  const copyMessageLink = useCallback((messageId: string) => {
    const url = `${window.location.origin}${window.location.pathname}?msg=${messageId}`
    navigator.clipboard.writeText(url)
  }, [])

  const startEdit = useCallback((id: Id<'communityMessages'>, content: string) => {
    setEditingId(id)
    setEditText(content)
  }, [])

  const submitEdit = useCallback(() => {
    if (!editingId || !editText.trim()) return
    editMessage(editingId, editText)
    setEditingId(null)
    setEditText('')
  }, [editingId, editText, editMessage])

  const [isJoining, setIsJoining] = useState(false)
  const handleJoin = useCallback(async () => {
    if (isJoining) return
    setIsJoining(true)
    try {
      await join()
    } finally {
      setIsJoining(false)
    }
  }, [join, isJoining])

  // Group messages by date
  const groupedMessages = useMemo(() => {
    const groups: { date: string; messages: typeof messages }[] = []
    let currentDate = ''

    for (const msg of messages) {
      const d = new Date(msg.createdAt)
      const dateStr = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
      if (dateStr !== currentDate) {
        currentDate = dateStr
        groups.push({ date: dateStr, messages: [] })
      }
      groups[groups.length - 1].messages.push(msg)
    }

    return groups
  }, [messages])

  if (!open) return null

  return (
    <div className="w-full max-w-md flex flex-col bg-white border-l border-neutral-200 animate-in slide-in-from-right duration-200 flex-shrink-0">
      {/* Header */}
      <div className="flex items-center justify-between px-4 h-14 border-b border-neutral-200 flex-shrink-0">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-neutral-500" />
          <span className="font-semibold text-sm">Community</span>
        </div>
        <div className="flex items-center gap-1">
          {isMember && (
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setShowMenu((v) => !v)}
                className="p-1.5 rounded-lg hover:bg-neutral-100 transition-colors"
              >
                <MoreVertical className="h-4 w-4 text-neutral-400" />
              </button>
              {showMenu && (
                <div className="absolute right-0 top-full mt-1 w-44 rounded-lg border border-neutral-200 bg-white shadow-lg py-1 z-50">
                  <button
                    onClick={handleLeave}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-neutral-600 hover:bg-neutral-50 transition-colors"
                  >
                    <LogOut className="h-3.5 w-3.5" />
                    Leave community
                  </button>
                </div>
              )}
            </div>
          )}
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-neutral-100 transition-colors">
            <X className="h-4 w-4 text-neutral-500" />
          </button>
        </div>
      </div>

      {/* Not joined yet */}
      {!isMember && !isLoading && (
        <div
          className="flex-1 flex items-start justify-center px-5 pt-10 pb-6 relative"
          style={{
            backgroundColor: '#efefef',
            backgroundImage: 'url(/community-pattern.jpg)',
            backgroundSize: '420px auto',
            backgroundRepeat: 'repeat',
          }}
        >
          <div className="w-full max-w-sm rounded-2xl bg-white border border-neutral-200 p-8 shadow-sm relative">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-2xl bg-neutral-50 ring-1 ring-neutral-200 flex items-center justify-center mb-5 overflow-hidden">
                <img src="/logo-sq-black.png" alt="Footage" className="w-10 h-10 object-contain" />
              </div>
              <p className="text-sm text-neutral-500 mb-1">You've been invited to join</p>
              <h3 className="text-2xl font-bold tracking-tight text-neutral-900 mb-4">Footage Community</h3>
              <div className="flex items-center gap-5 mb-6 text-sm text-neutral-600">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-500" />
                  <span><span className="font-semibold text-neutral-900">{(stats?.online ?? 0).toLocaleString()}</span> Online</span>
                </span>
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-neutral-300 ring-1 ring-neutral-400/40" />
                  <span><span className="font-semibold text-neutral-900">{(stats?.members ?? 0).toLocaleString()}</span> Members</span>
                </span>
              </div>
              <button
                onClick={handleJoin}
                disabled={isJoining}
                className="w-full px-6 py-3 bg-emerald-500 hover:bg-emerald-600 disabled:hover:bg-emerald-500 disabled:opacity-90 text-white text-sm font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {isJoining ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Joining…
                  </>
                ) : (
                  'Accept Invite'
                )}
              </button>
              <p className="text-xs text-neutral-500 mt-4 max-w-[260px]">
                Talk directly with the founders. Share ideas, request features, and help shape Footage.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Chat area */}
      {isMember && (
        <>
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className="flex-1 min-h-0 overflow-y-auto px-4 pt-4 pb-4 space-y-0.5"
          >
            {messages.length === 0 && !isLoading && (
              <div className="flex-1 flex flex-col items-center justify-center text-center py-16">
                <MessageCircle className="w-10 h-10 text-neutral-300 mb-3" />
                <p className="text-sm text-neutral-400">No messages yet. Say hello!</p>
              </div>
            )}

            {groupedMessages.map((group) => {
              let newDividerShown = false
              return (
              <div key={group.date}>
                {/* Date separator */}
                <div className="flex items-center gap-3 my-4 first:mt-0">
                  <div className="flex-1 h-px bg-neutral-200" />
                  <span className="text-[10px] font-medium text-neutral-400 uppercase tracking-wider">{group.date}</span>
                  <div className="flex-1 h-px bg-neutral-200" />
                </div>

                {group.messages.map((msg, i) => {
                  const isMe = msg.userId === user?.userId
                  const prevMsg = i > 0 ? group.messages[i - 1] : null
                  const isConsecutive = prevMsg?.userId === msg.userId &&
                    msg.createdAt - prevMsg.createdAt < 120000 // 2 min

                  // "New since last visit" divider
                  const savedLastSeen = lastSeenAtRef.current
                  let showNewDivider = false
                  if (
                    !newDividerShown &&
                    savedLastSeen &&
                    msg.createdAt > savedLastSeen &&
                    !isMe
                  ) {
                    showNewDivider = true
                    newDividerShown = true
                  }

                  return (
                    <div key={msg._id}>
                      {showNewDivider && (
                        <div className="flex items-center gap-3 my-3" ref={highlightedRef}>
                          <div className="flex-1 h-px bg-red-400" />
                          <span className="text-[10px] font-semibold text-red-500 uppercase tracking-wider">New</span>
                          <div className="flex-1 h-px bg-red-400" />
                        </div>
                      )}
                      <MessageBubble
                        msg={msg}
                        isMe={isMe}
                        isConsecutive={isConsecutive && !showNewDivider}
                        editingId={editingId}
                        editText={editText}
                        setEditText={setEditText}
                        onStartEdit={startEdit}
                        onSubmitEdit={submitEdit}
                        onCancelEdit={() => { setEditingId(null); setEditText('') }}
                        onDelete={deleteMessage}
                        onAdminHide={adminHideMessage}
                        onUnhide={unhideMessage}
                        onShadowBan={shadowBanUser}
                        onUnShadowBan={unShadowBanUser}
                        onReply={(id, name, content) => {
                          setReplyTo({ _id: id, authorName: name, content })
                          inputRef.current?.focus()
                        }}
                        onToggleReaction={toggleReaction}
                        onCopyLink={copyMessageLink}
                        memberNames={members.map(m => m.name)}
                        emojiPickerFor={emojiPickerFor}
                        setEmojiPickerFor={setEmojiPickerFor}
                        emojiPickerRef={emojiPickerRef}
                        currentUserId={user?.userId}
                        isAdmin={isAdmin}
                      />
                    </div>
                  )
                })}
              </div>
              )
            })}

            {/* Typing indicator */}
            {typingUsers.length > 0 && (
              <div className="flex items-center gap-2 py-2 px-1 animate-in fade-in duration-200">
                <div className="flex gap-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-neutral-400 animate-bounce [animation-delay:0ms]" />
                  <span className="w-1.5 h-1.5 rounded-full bg-neutral-400 animate-bounce [animation-delay:150ms]" />
                  <span className="w-1.5 h-1.5 rounded-full bg-neutral-400 animate-bounce [animation-delay:300ms]" />
                </div>
                <span className="text-xs text-neutral-400">
                  {typingUsers.length === 1
                    ? `${typingUsers[0]} is typing`
                    : `${typingUsers.slice(0, 2).join(', ')} are typing`}
                </span>
              </div>
            )}
          </div>

          {/* Scroll to bottom */}
          {isScrolledUp && (
            <button
              onClick={scrollToBottom}
              className="absolute bottom-24 right-4 z-10 w-8 h-8 rounded-full bg-white border border-neutral-200 shadow-md flex items-center justify-center hover:bg-neutral-50 transition-colors"
            >
              <ArrowDown size={16} className="text-neutral-600" />
              {newMessageCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] rounded-full bg-neutral-900 text-white text-[10px] font-medium flex items-center justify-center px-1">
                  {newMessageCount}
                </span>
              )}
            </button>
          )}

          {/* Reply indicator */}
          {replyTo && (
            <div className="mx-4 px-3 py-2 bg-neutral-100 rounded-t-lg border border-b-0 border-neutral-200 flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <Reply className="h-3.5 w-3.5 text-neutral-400 flex-shrink-0" />
                <span className="text-xs text-neutral-500 truncate">
                  Replying to <span className="font-medium text-neutral-700">{replyTo.authorName}</span>: {replyTo.content.slice(0, 60)}
                </span>
              </div>
              <button onClick={() => setReplyTo(null)} className="p-0.5 hover:bg-neutral-200 rounded">
                <X className="h-3 w-3 text-neutral-400" />
              </button>
            </div>
          )}

          {/* @mention autocomplete */}
          {filteredMembers.length > 0 && (
            <div className="mx-4 mb-1 bg-white border border-neutral-200 rounded-lg shadow-lg overflow-hidden">
              {filteredMembers.map((m, i) => (
                <button
                  key={m.userId}
                  onClick={() => insertMention(m.name)}
                  className={cn(
                    "w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors",
                    i === mentionIndex ? "bg-neutral-100" : "hover:bg-neutral-50"
                  )}
                >
                  {m.avatarUrl ? (
                    <img src={m.avatarUrl} alt="" className="w-5 h-5 rounded-full" />
                  ) : (
                    <div className="w-5 h-5 rounded-full bg-neutral-200 flex items-center justify-center text-[10px] font-medium text-neutral-600">
                      {m.name[0]?.toUpperCase()}
                    </div>
                  )}
                  <span className="font-medium text-neutral-800">{m.name}</span>
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className={cn("px-4 pb-4", replyTo ? "pt-0" : "pt-2")}>
            <div className={cn(
              "flex items-end gap-2 bg-neutral-100 border border-neutral-200 px-3 py-2",
              replyTo ? "rounded-b-lg" : "rounded-xl"
            )}>
              <textarea
                ref={inputRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="Say something..."
                rows={1}
                className="flex-1 bg-transparent text-sm resize-none outline-none placeholder:text-neutral-400 max-h-24 min-h-[24px]"
                style={{ height: 'auto', overflow: 'hidden' }}
                onInput={(e) => {
                  const t = e.currentTarget
                  t.style.height = 'auto'
                  t.style.height = Math.min(t.scrollHeight, 96) + 'px'
                }}
              />
              <button
                onClick={handleSend}
                disabled={!input.trim()}
                className={cn(
                  "w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-colors",
                  input.trim()
                    ? "bg-neutral-900 text-white hover:bg-neutral-800"
                    : "bg-neutral-200 text-neutral-400"
                )}
              >
                <ArrowUp size={14} />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// Backdrop overlay
export function CommunityPanelBackdrop({ open, onClose }: { open: boolean; onClose: () => void }) {
  if (!open) return null
  return (
    <div
      className="fixed inset-0 z-40 bg-black/20 animate-in fade-in duration-200"
      onClick={onClose}
    />
  )
}

// --- Message bubble ---

interface MessageBubbleProps {
  msg: {
    _id: Id<'communityMessages'>
    userId: string
    content: string
    isDeleted: boolean
    adminHidden?: boolean
    replyTo?: { _id: string; content: string; authorName: string }
    reactions: { emoji: string; userIds: string[] }[]
    editedAt?: number
    createdAt: number
    author: { userId: string; name: string; avatarUrl?: string; isAdmin?: boolean; isShadowBanned?: boolean }
  }
  isMe: boolean
  isConsecutive: boolean
  editingId: Id<'communityMessages'> | null
  editText: string
  setEditText: (t: string) => void
  onStartEdit: (id: Id<'communityMessages'>, content: string) => void
  onSubmitEdit: () => void
  onCancelEdit: () => void
  onDelete: (id: Id<'communityMessages'>) => void
  onAdminHide: (id: Id<'communityMessages'>) => void
  onUnhide: (id: Id<'communityMessages'>) => void
  onShadowBan: (userId: string) => void
  onUnShadowBan: (userId: string) => void
  onReply: (id: Id<'communityMessages'>, name: string, content: string) => void
  onToggleReaction: (id: Id<'communityMessages'>, emoji: string) => void
  onCopyLink: (id: string) => void
  memberNames: string[]
  emojiPickerFor: Id<'communityMessages'> | null
  setEmojiPickerFor: (id: Id<'communityMessages'> | null) => void
  emojiPickerRef: React.RefObject<HTMLDivElement | null>
  currentUserId?: string
  isAdmin?: boolean
}

function MessageBubble({
  msg,
  isMe,
  isConsecutive,
  editingId,
  editText,
  setEditText,
  onStartEdit,
  onSubmitEdit,
  onCancelEdit,
  onDelete,
  onAdminHide,
  onUnhide,
  onShadowBan,
  onUnShadowBan,
  onReply,
  onToggleReaction,
  onCopyLink,
  memberNames,
  emojiPickerFor,
  setEmojiPickerFor,
  emojiPickerRef,
  currentUserId,
  isAdmin,
}: MessageBubbleProps) {
  const [hovering, setHovering] = useState(false)
  const isEditing = editingId === msg._id
  const time = new Date(msg.createdAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })

  if (msg.isDeleted && !isAdmin) {
    return null
  }

  if (msg.isDeleted && isAdmin) {
    return (
      <div
        id={`msg-${msg._id}`}
        className={cn(
          "py-0.5 px-1 rounded-lg",
          !isConsecutive && "mt-3",
        )}
      >
        <span className="text-xs italic text-neutral-400">message deleted</span>
      </div>
    )
  }

  // Extract URLs and images from content
  const urls = msg.content.match(URL_REGEX) || []
  const imageUrls = urls.filter((u) => IMAGE_EXTENSIONS.test(u))

  return (
    <div
      id={`msg-${msg._id}`}
      className={cn(
        "group relative py-0.5 px-1 rounded-lg transition-colors",
        hovering && "bg-neutral-50",
        !isConsecutive && "mt-3",
        msg.adminHidden && isAdmin && "opacity-50 border-l-2 border-red-400 pl-2 bg-red-50/50",
        !msg.adminHidden && msg.author.isShadowBanned && isAdmin && "opacity-50 border-l-2 border-orange-400 pl-2 bg-orange-50/50",
      )}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      {/* Author header (skip for consecutive) */}
      {!isConsecutive && (
        <div className="flex items-center gap-2 mb-0.5">
          {msg.author.avatarUrl ? (
            <img src={msg.author.avatarUrl} alt="" className="w-5 h-5 rounded-full" />
          ) : (
            <div className="w-5 h-5 rounded-full bg-neutral-200 flex items-center justify-center text-[10px] font-medium text-neutral-600">
              {msg.author.name[0]?.toUpperCase()}
            </div>
          )}
          <span className="text-xs font-semibold text-neutral-900">{msg.author.name}</span>
          {msg.author.isAdmin && (
            <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full">founder</span>
          )}
          {isAdmin && msg.author.isShadowBanned && (
            <span className="text-[10px] font-medium text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded-full">shadow banned</span>
          )}
          {isAdmin && msg.adminHidden && (
            <span className="text-[10px] font-medium text-red-600 bg-red-50 px-1.5 py-0.5 rounded-full">hidden</span>
          )}
          <span className="text-[10px] text-neutral-400">{time}</span>
          {msg.editedAt && <span className="text-[10px] text-neutral-400">(edited)</span>}
        </div>
      )}

      {/* Reply context */}
      {msg.replyTo && (
        <div className="ml-7 mb-1 px-2 py-1 border-l-2 border-neutral-300 bg-neutral-50 rounded-r text-xs text-neutral-500">
          <span className="font-medium text-neutral-600">{msg.replyTo.authorName}</span>{': '}
          {msg.replyTo.content || <em>deleted</em>}
        </div>
      )}

      {/* Content */}
      <div className={cn("text-sm text-neutral-800 leading-relaxed", !isConsecutive ? "ml-7" : "ml-7")}>
        {isEditing ? (
          <div className="flex items-center gap-1.5">
            <input
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onSubmitEdit()
                if (e.key === 'Escape') onCancelEdit()
              }}
              className="flex-1 text-sm bg-white border border-neutral-300 rounded px-2 py-1 outline-none focus:border-neutral-400"
              autoFocus
            />
            <button onClick={onSubmitEdit} className="p-1 hover:bg-neutral-200 rounded">
              <Check className="h-3.5 w-3.5 text-green-600" />
            </button>
            <button onClick={onCancelEdit} className="p-1 hover:bg-neutral-200 rounded">
              <X className="h-3.5 w-3.5 text-neutral-400" />
            </button>
          </div>
        ) : (
          <div className="prose prose-sm prose-neutral max-w-none [&_p]:my-0 [&_a]:text-blue-600 [&_a]:underline [&_a]:decoration-blue-300 [&_code]:bg-neutral-100 [&_code]:px-1 [&_code]:rounded [&_code]:text-xs [&_pre]:bg-neutral-100 [&_pre]:rounded-lg [&_pre]:p-2">
            <MentionAwareMarkdown content={msg.content} memberNames={memberNames} />
          </div>
        )}

        {/* Inline images */}
        {imageUrls.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {imageUrls.map((url, i) => (
              <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="block">
                <img
                  src={url}
                  alt=""
                  className="max-w-[240px] max-h-[180px] rounded-lg border border-neutral-200 object-cover"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                />
              </a>
            ))}
          </div>
        )}
      </div>

      {/* Reactions */}
      {msg.reactions.length > 0 && (
        <div className="flex flex-wrap gap-1 ml-7 mt-1">
          {msg.reactions.map((r) => {
            const iReacted = r.userIds.includes(currentUserId ?? '')
            return (
              <button
                key={r.emoji}
                onClick={() => onToggleReaction(msg._id, r.emoji)}
                className={cn(
                  "inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs border transition-colors",
                  iReacted
                    ? "bg-blue-50 border-blue-200 text-blue-700"
                    : "bg-white border-neutral-200 text-neutral-600 hover:bg-neutral-50"
                )}
              >
                <span>{r.emoji}</span>
                <span className="text-[10px] font-medium">{r.userIds.length}</span>
              </button>
            )
          })}
        </div>
      )}

      {/* Hover actions */}
      {hovering && !isEditing && (
        <div className="absolute -top-3 right-2 flex items-center bg-white border border-neutral-200 rounded-lg shadow-sm overflow-hidden">
          <button
            onClick={() => setEmojiPickerFor(emojiPickerFor === msg._id ? null : msg._id)}
            className="p-1.5 hover:bg-neutral-100 transition-colors"
            title="React"
          >
            <Smile className="h-3.5 w-3.5 text-neutral-500" />
          </button>
          <button
            onClick={() => onReply(msg._id, msg.author.name, msg.content)}
            className="p-1.5 hover:bg-neutral-100 transition-colors"
            title="Reply"
          >
            <Reply className="h-3.5 w-3.5 text-neutral-500" />
          </button>
          <button
            onClick={() => onCopyLink(msg._id)}
            className="p-1.5 hover:bg-neutral-100 transition-colors"
            title="Copy link"
          >
            <Link2 className="h-3.5 w-3.5 text-neutral-500" />
          </button>
          {isMe && (
            <button
              onClick={() => onStartEdit(msg._id, msg.content)}
              className="p-1.5 hover:bg-neutral-100 transition-colors"
              title="Edit"
            >
              <Pencil className="h-3.5 w-3.5 text-neutral-500" />
            </button>
          )}
          {(isMe || isAdmin) && (
            <button
              onClick={() => onDelete(msg._id)}
              className="p-1.5 hover:bg-neutral-100 transition-colors"
              title="Delete for everyone"
            >
              <Trash2 className="h-3.5 w-3.5 text-neutral-500" />
            </button>
          )}
          {isAdmin && !isMe && (
            <>
              {msg.adminHidden ? (
                <button
                  onClick={() => onUnhide(msg._id)}
                  className="p-1.5 hover:bg-neutral-100 transition-colors"
                  title="Unhide message"
                >
                  <Eye className="h-3.5 w-3.5 text-green-500" />
                </button>
              ) : (
                <button
                  onClick={() => onAdminHide(msg._id)}
                  className="p-1.5 hover:bg-neutral-100 transition-colors"
                  title="Hide for others (visible to you)"
                >
                  <EyeOff className="h-3.5 w-3.5 text-orange-500" />
                </button>
              )}
              {msg.author.isShadowBanned ? (
                <button
                  onClick={() => onUnShadowBan(msg.userId)}
                  className="p-1.5 hover:bg-neutral-100 transition-colors"
                  title="Remove shadow ban"
                >
                  <ShieldOff className="h-3.5 w-3.5 text-green-500" />
                </button>
              ) : (
                <button
                  onClick={() => onShadowBan(msg.userId)}
                  className="p-1.5 hover:bg-neutral-100 transition-colors"
                  title="Shadow ban user"
                >
                  <Ban className="h-3.5 w-3.5 text-red-500" />
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* Emoji quick picker */}
      {emojiPickerFor === msg._id && (
        <div
          ref={emojiPickerRef as React.RefObject<HTMLDivElement>}
          className="absolute -top-10 right-2 flex items-center gap-0.5 bg-white border border-neutral-200 rounded-xl shadow-lg px-1.5 py-1 z-20 animate-in fade-in zoom-in-95 duration-100"
        >
          {EMOJI_QUICK.map((emoji) => (
            <button
              key={emoji}
              onClick={() => {
                onToggleReaction(msg._id, emoji)
                setEmojiPickerFor(null)
              }}
              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-neutral-100 transition-colors text-base"
            >
              {emoji}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

const MARKDOWN_LINK_COMPONENTS = {
  a: ({ node: _node, ...props }: { node?: unknown } & React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
    <a {...props} target="_blank" rel="noopener noreferrer" />
  ),
}

function MentionAwareMarkdown({ content, memberNames }: { content: string; memberNames: string[] }) {
  const mentionRegex = useMemo(() => buildMentionRegex(memberNames), [memberNames])

  if (!mentionRegex || !mentionRegex.test(content)) {
    mentionRegex && (mentionRegex.lastIndex = 0)
    return (
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_LINK_COMPONENTS}>
        {content}
      </ReactMarkdown>
    )
  }
  mentionRegex.lastIndex = 0

  const parts: (string | { mention: string })[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  while ((match = mentionRegex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      parts.push(content.slice(lastIndex, match.index))
    }
    parts.push({ mention: match[0] })
    lastIndex = match.index + match[0].length
  }
  if (lastIndex < content.length) {
    parts.push(content.slice(lastIndex))
  }

  return (
    <p>
      {parts.map((part, i) =>
        typeof part === 'string' ? (
          <ReactMarkdown
            key={i}
            remarkPlugins={[remarkGfm]}
            components={{ ...MARKDOWN_LINK_COMPONENTS, p: ({ children }) => <>{children}</> }}
          >
            {part}
          </ReactMarkdown>
        ) : (
          <span key={i} className="font-semibold text-blue-600 bg-blue-50 px-0.5 rounded">{part.mention}</span>
        )
      )}
    </p>
  )
}
